import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { useToast } from "@/features/toast/ToastProvider";
import { useConfirm, usePrompt } from "@/features/confirm/ConfirmProvider";
import { ImpersonateButton } from "@/features/auth/ImpersonateButton";
import { formatDate, formatMoney } from "@bookboost/shared/format";

interface CommissionDetail {
  id: string;
  user_id: string | null;
  deal_id: string | null;
  invoice_id: string | null;
  deal_amount: number | null;
  commission_total: number;
  cash_portion: number;
  equity_portion: number;
  currency: string;
  status: "pending" | "earned" | "approved" | "paid";
  earned_at: string | null;
  approved_at: string | null;
  paid_at: string | null;
  notes: string | null;
  reversal_of_id: string | null;
  superseded_at: string | null;
  users: { email: string; full_name: string | null; hubspot_owner_id: string | null } | null;
  deals: {
    id: string;
    deal_name: string | null;
    hubspot_deal_id: string;
    stage: string | null;
    amount: number | null;
    currency: string | null;
    closed_won_at: string | null;
  } | null;
  invoices: {
    id: string;
    stripe_invoice_id: string;
    amount: number | null;
    amount_paid: number | null;
    currency: string | null;
    status: string;
    paid_at: string | null;
    created_at_stripe: string | null;
  } | null;
  paid_by_user: { email: string; full_name: string | null } | null;
}

interface FlagRow {
  id: string;
  reason: string;
  status: "open" | "resolved";
  resolution_note: string | null;
  resolved_at: string | null;
  created_at: string;
  raised_by: { email: string; full_name: string | null } | null;
}

interface DetailResponse {
  commission: CommissionDetail;
  reversalOf: { id: string; commission_total: number; currency: string; status: string } | null;
  reversals: Array<{ id: string; commission_total: number; currency: string; status: string }>;
  stageLabels: Array<{ stage_id: string; stage_label: string }>;
  flags: FlagRow[];
}

export const Route = createFileRoute("/admin/commissions_/$id")({
  component: CommissionDetailPage,
});

function CommissionDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const promptInput = usePrompt();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "commission", id],
    queryFn: () => api.get<DetailResponse>(`/api/commissions/${id}`),
  });

  const [notes, setNotes] = useState("");
  useEffect(() => {
    if (data?.commission.notes != null) setNotes(data.commission.notes);
  }, [data?.commission.notes]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "commission"] });
    qc.invalidateQueries({ queryKey: ["admin", "commissions"] });
    qc.invalidateQueries({ queryKey: ["admin", "overview"] });
    qc.invalidateQueries({ queryKey: ["admin", "payouts"] });
    qc.invalidateQueries({ queryKey: ["finance"] });
  };

  const saveNotes = useMutation({
    mutationFn: (n: string | null) => api.patch(`/api/commissions/${id}/notes`, { notes: n }),
    onSuccess: () => {
      toast.ok("Notes saved.");
      invalidate();
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't save notes."),
  });

  const reverse = useMutation({
    mutationFn: (reason: string) => api.post<{ kind: string; reversalId?: string; reason?: string }>(`/api/commissions/${id}/reverse`, { reason }),
    onSuccess: (res) => {
      if (res.kind === "skipped") {
        toast.warn(`Already reversed (${res.reason ?? "skipped"}).`);
      } else {
        toast.ok("Commission reversed. Net is now zero across the pair.");
      }
      invalidate();
      if (res.reversalId) navigate({ to: "/admin/commissions/$id", params: { id: res.reversalId } });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't reverse."),
  });

  const recalc = useMutation({
    mutationFn: ({ reason, includePaid }: { reason: string; includePaid: boolean }) =>
      api.post<{
        kind: string;
        newCommissionId?: string;
        oldTotal?: number;
        newTotal?: number;
        currency?: string;
        reason?: string;
      }>(`/api/commissions/${id}/recalculate`, { reason, includePaid }),
    onSuccess: (res) => {
      if (res.kind === "skipped") {
        toast.warn(`Recalc skipped (${res.reason ?? "skipped"}).`);
      } else {
        const oldM = formatMoney(res.oldTotal ?? 0, res.currency ?? "");
        const newM = formatMoney(res.newTotal ?? 0, res.currency ?? "");
        toast.ok(`Recalculated: ${oldM} → ${newM}. Original superseded; reversal + new earned row written.`, 8000);
      }
      invalidate();
      if (res.newCommissionId) navigate({ to: "/admin/commissions/$id", params: { id: res.newCommissionId } });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't recalculate."),
  });

  async function onReverse() {
    const reason = await promptInput({
      title: "Reverse commission",
      prompt: "Reverse this commission? A new row is written with reversal_of set; net is zero across the pair.",
      label: "Reason (lands in the audit trail)",
      placeholder: "e.g. attributed to the wrong rep",
      tone: "destructive",
      confirmLabel: "Reverse",
    });
    if (reason) reverse.mutate(reason);
  }
  async function onRecalc() {
    const reason = await promptInput({
      title: "Recalculate commission",
      prompt: "Recalculate using the structure active at earned_at? The current row is superseded; a reversal + a fresh earned row are written.",
      label: "Reason (lands in the audit trail)",
      confirmLabel: "Recalculate",
    });
    if (!reason) return;
    let includePaid = false;
    if (data?.commission.status === "paid") {
      includePaid = await confirm({
        title: "This commission is paid",
        prompt: "Include paid rows in the recalc? Cash already disbursed will NOT be clawed back automatically — payroll settles the delta on the next run.",
        tone: "destructive",
        confirmLabel: "Include paid",
        cancelLabel: "Skip",
      });
    }
    recalc.mutate({ reason, includePaid });
  }

  const resolveFlag = useMutation({
    mutationFn: ({ flagId, note }: { flagId: string; note: string }) =>
      api.post(`/api/commissions/${id}/resolve-flag`, { flagId, note: note || undefined }),
    onSuccess: () => {
      toast.ok("Flag resolved.");
      qc.invalidateQueries({ queryKey: ["admin", "commission"] });
      qc.invalidateQueries({ queryKey: ["admin", "commissions"] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't resolve."),
  });
  async function onResolveFlag(flagId: string) {
    const note = await promptInput({
      title: "Resolve flag",
      prompt: "Note the outcome (optional) — what did you find / do?",
      label: "Resolution note",
      required: false,
      confirmLabel: "Mark resolved",
    });
    if (note === null) return; // cancelled
    resolveFlag.mutate({ flagId, note });
  }

  if (isLoading) return <p className="lede">Loading commission…</p>;
  if (error || !data) {
    return (
      <Panel variant="err">
        <h2>Commission not found</h2>
        <p>{error instanceof Error ? error.message : "It may have been deleted."}</p>
        <p>
          <a href="/admin/commissions">← All commissions</a>
        </p>
      </Panel>
    );
  }

  const c = data.commission;
  const stageLabel = (id: string | null) =>
    id ? data.stageLabels.find((s) => s.stage_id === id)?.stage_label ?? id : "—";

  return (
    <>
      <p style={{ marginBottom: 12 }}>
        <a href="/admin/commissions">← All commissions</a>
      </p>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>
          {formatMoney(c.commission_total, c.currency)}
          <span className="muted" style={{ fontSize: 16, fontWeight: 500, marginLeft: 12 }}>
            · {c.users ? c.users.full_name ?? c.users.email : "—"}
          </span>
        </h1>
        <StatusBadge status={c.status} suffix={c.reversal_of_id ? "(reversal)" : undefined} />
      </div>

      {data.flags.length > 0 && (
        <Panel title="Flags" variant={data.flags.some((f) => f.status === "open") ? "warn" : undefined}>
          {data.flags.map((f) => (
            <div
              key={f.id}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, padding: "8px 0", borderBottom: "1px solid var(--border)" }}
            >
              <div>
                <p style={{ margin: "0 0 2px" }}>
                  <span className={`badge ${f.status === "open" ? "pending" : ""}`}>{f.status === "open" ? "open" : "resolved"}</span>{" "}
                  {f.reason}
                </p>
                <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                  raised by {f.raised_by?.full_name ?? f.raised_by?.email ?? "rep"} · {formatDate(f.created_at)}
                  {f.status === "resolved" && f.resolution_note && ` · resolved: ${f.resolution_note}`}
                </p>
              </div>
              {f.status === "open" && (
                <button
                  type="button"
                  style={{ padding: "4px 10px", fontSize: 12, whiteSpace: "nowrap" }}
                  disabled={resolveFlag.isPending}
                  onClick={() => onResolveFlag(f.id)}
                >
                  Resolve
                </button>
              )}
            </div>
          ))}
        </Panel>
      )}

      <div className="grid cols-3">
        <Panel title="Commission">
          <Defs>
            <Def label="ID" value={<code>{c.id}</code>} />
            <Def label="Status" value={<StatusBadge status={c.status} />} />
            <Def label="Total" value={formatMoney(c.commission_total, c.currency)} />
            <Def label="Cash portion" value={formatMoney(c.cash_portion, c.currency)} />
            <Def label="Equity portion" value={formatMoney(c.equity_portion, c.currency)} />
            <Def label="Basis (deal amount)" value={formatMoney(c.deal_amount, c.currency)} />
            <Def label="Earned" value={formatDate(c.earned_at)} />
            <Def label="Approved" value={formatDate(c.approved_at)} />
            <Def label="Paid" value={formatDate(c.paid_at)} />
            {c.paid_by_user && (
              <Def label="Paid by" value={c.paid_by_user.full_name ?? c.paid_by_user.email} />
            )}
            {c.superseded_at && <Def label="Superseded" value={formatDate(c.superseded_at)} />}
          </Defs>
        </Panel>

        <Panel title="Rep">
          {c.users && c.user_id ? (
            <>
              <Defs>
                <Def label="Name" value={c.users.full_name ?? "—"} />
                <Def label="Email" value={<code>{c.users.email}</code>} />
                <Def label="HubSpot owner" value={c.users.hubspot_owner_id ?? "—"} />
              </Defs>
              <div style={{ marginTop: 12 }}>
                <ImpersonateButton
                  userId={c.user_id}
                  label={c.users.full_name ?? c.users.email}
                  buttonLabel="View as this rep"
                />
              </div>
            </>
          ) : (
            <p className="muted">No rep attached.</p>
          )}
        </Panel>

        <Panel title="Sources">
          <Defs>
            {c.deals && (
              <>
                <Def label="Deal" value={c.deals.deal_name ?? "—"} />
                <Def label="Stage" value={stageLabel(c.deals.stage)} />
                <Def label="HubSpot deal" value={<code>{c.deals.hubspot_deal_id}</code>} />
                <Def label="Closed won" value={formatDate(c.deals.closed_won_at)} />
              </>
            )}
            {c.invoices && (
              <>
                <Def label="Invoice" value={<code>{c.invoices.stripe_invoice_id}</code>} />
                <Def label="Invoice paid" value={formatMoney(c.invoices.amount_paid, c.invoices.currency)} />
                <Def label="Invoice status" value={c.invoices.status} />
              </>
            )}
          </Defs>
        </Panel>
      </div>

      {(data.reversalOf || data.reversals.length > 0) && (
        <Panel title="Reversal chain" variant="warn">
          {data.reversalOf && (
            <p>
              <strong>Reverses</strong>{" "}
              <a href={`/admin/commissions/${data.reversalOf.id}`}>
                {formatMoney(data.reversalOf.commission_total, data.reversalOf.currency)} · {data.reversalOf.status}
              </a>
            </p>
          )}
          {data.reversals.length > 0 && (
            <p>
              <strong>Reversed by</strong>{" "}
              {data.reversals.map((r, i) => (
                <span key={r.id}>
                  <a href={`/admin/commissions/${r.id}`}>
                    {formatMoney(r.commission_total, r.currency)} · {r.status}
                  </a>
                  {i < data.reversals.length - 1 && ", "}
                </span>
              ))}
            </p>
          )}
        </Panel>
      )}

      <Panel title="Notes">
        <textarea
          rows={3}
          style={{ width: "100%", fontFamily: "var(--font-body)" }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional audit notes — visible to admins."
        />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <button
            className="primary"
            disabled={saveNotes.isPending}
            onClick={() => saveNotes.mutate(notes.trim() || null)}
          >
            {saveNotes.isPending ? "Saving…" : "Save notes"}
          </button>
        </div>
      </Panel>

      <Panel title="Audit actions" variant="warn">
        <p className="muted" style={{ fontSize: 12.5 }}>
          Commissions are immutable. To correct one, write a reversal (zeroing out the original)
          or recalculate (reverse + supersede + recompute against the structure active at earned_at).
        </p>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button
            style={{ borderColor: "var(--err)", color: "var(--err)" }}
            disabled={reverse.isPending}
            onClick={onReverse}
          >
            {reverse.isPending ? "Reversing…" : "Reverse"}
          </button>
          <button className="primary" disabled={recalc.isPending} onClick={onRecalc}>
            {recalc.isPending ? "Recalculating…" : "Recalculate"}
          </button>
        </div>
      </Panel>
    </>
  );
}

function Defs({ children }: { children: React.ReactNode }) {
  return (
    <table>
      <tbody>{children}</tbody>
    </table>
  );
}
function Def({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <tr>
      <th style={{ width: 180, textAlign: "left", fontWeight: 500, fontSize: 12, color: "var(--muted)" }}>{label}</th>
      <td>{value}</td>
    </tr>
  );
}
